import React from 'react';
import CopyableBlock from './CopyableBlock';
import { getTableCopyContent } from './tableUtils';

/**
 * Helper function to extract text from React nodes
 */
export const getNodeText = (node) => {
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(getNodeText).join('');
  if (node && typeof node === 'object' && 'props' in node) {
    return getNodeText(node.props.children);
  }
  return '';
};

/**
 * Factory function to create markdown components with optional handlers
 * @param {Object} options
 * @param {Function} [options.onInsertTable] - Handler called with table HTML when user clicks insert
 */
export const createMarkdownComponents = ({ onInsertTable } = {}) => ({
  table: ({ node: _node, ...props }) => (
    <CopyableBlock
      className="my-4"
      getCopyContent={(container) => getTableCopyContent(container, 'default')}
      copyOptions={[
        { label: 'Markdown', getContent: (container) => getTableCopyContent(container, 'markdown') },
        { label: 'HTML', getContent: (container) => getTableCopyContent(container, 'html') },
        { label: 'JSON', getContent: (container) => getTableCopyContent(container, 'json') },
        { label: 'CSV', getContent: (container) => getTableCopyContent(container, 'csv') },
      ]}
      onInsert={onInsertTable ? (container) => {
        const table = container?.querySelector('table');
        if (table) {
          onInsertTable(table.outerHTML);
        }
      } : undefined}
    >
      <div className="chat-table-wrapper">
        <table {...props} />
      </div>
    </CopyableBlock>
  ),
  pre: ({ node: _node, children }) => {
    const codeChild = React.Children.toArray(children)[0];
    const textContent = getNodeText(codeChild || '').replace(/\n+$/, '');
    const isMultiline = textContent.includes('\n');

    if (!isMultiline) {
      return (
        <code className={codeChild?.props?.className}>
          {textContent}
        </code>
      );
    }

    return (
      <CopyableBlock className="my-3">
        <pre className="chat-code-block">
          {children}
        </pre>
      </CopyableBlock>
    );
  },
  code: ({ inline, className, children, ...props }) => {
    if (inline) {
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }

    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  blockquote: ({ node: _node, ...props }) => (
    <CopyableBlock className="my-3">
      <blockquote {...props} />
    </CopyableBlock>
  ),
});

// Backward-compatible default export (no insert handler)
export const markdownComponents = createMarkdownComponents();
